import { Card, CardContent } from "@/components/ui/card";
import { useRunDnsCheck } from "@/hooks/use-postmaster";
import { InfoIcon, Loader2 } from "lucide-react";

import {
  EmailTestProgressLoader,
  TestCompletedNotification,
} from "./email-test-progress-loader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useUsageLimitChecker } from "@/hooks/use-usage-limit-checker";
import { cn } from "@/lib/utils";
import { ArrowsClockwise } from "@phosphor-icons/react";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

const RunEmailTest = ({
  isMailboxConnected,
  domain,
  espType,
}: {
  isMailboxConnected: boolean;
  domain: string;
  espType: string;
}) => {
  const [showNoMailboxDialog, setShowNoMailboxDialog] = useState(false);
  const [isTestCompleted, setIsTestCompleted] = useState(false);

  const [isRunningTest, setIsRunningTest] = useState(false);

  const { isUsageLimitReached } = useUsageLimitChecker();
  const runDnsCheck = useRunDnsCheck(domain);
  const navigate = useNavigate();

  const handleCheckClick = async () => {
    try {
      if (!isMailboxConnected) {
        setShowNoMailboxDialog(true);

        return;
      }

      if (isUsageLimitReached("limit_healthTestsUsedThisMonth")) {
        toast.error(
          "You've reached the maximum limit of email health tests for this month."
        );
        return;
      }

      await runDnsCheck.mutateAsync({ domain, espType: espType });
      setIsRunningTest(true);
      window.cioanalytics?.track("email_health_test_run_connected", {
        from: "report",
        domain: domain,
      });
    } catch (error) {
      console.error(error);
    }
  };

  if (isRunningTest) {
    return (
      <EmailTestProgressLoader
        domain={domain}
        isRunningTest={isRunningTest}
        onComplete={() => {
          setIsRunningTest(false);
          setIsTestCompleted(true);
        }}
      />
    );
  }

  if (isTestCompleted) {
    return (
      <TestCompletedNotification
        domain={domain}
        onRefresh={() => setIsTestCompleted(false)}
      />
    );
  }

  return (
    <>
      <Card
        className={cn(
          "col-span-3 mt-5 bg-gradient-to-br from-blue-50 to-blue-100/50 overflow-hidden border-blue-200/50 shadow-sm dark:from-blue-900/20 dark:to-blue-900/20 dark:border-blue-900/20"
        )}
      >
        <CardContent className="p-0">
          <div className="flex flex-col md:flex-row md:items-center justify-between p-6">
            <div className="mb-4 md:mb-0">
              <h3 className="text-lg font-medium text-blue-800 dark:text-blue-400">
                Run Another Test
              </h3>
              <p className="mt-1 text-sm text-blue-700/80 dark:text-blue-400">
                Check your score with the latest data
              </p>
            </div>
            <Button
              onClick={handleCheckClick}
              disabled={runDnsCheck.isPending || isRunningTest}
            >
              {runDnsCheck.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ArrowsClockwise className="w-4 h-4" />
              )}
              Start New Test
            </Button>
          </div>
          <div className="bg-blue-100 border-t border-blue-200 px-6 py-3 flex items-center text-sm text-blue-700 dark:bg-blue-900/20 dark:border-blue-900/20 dark:text-blue-400">
            <InfoIcon className="h-4 w-4 mr-2 flex-shrink-0" />
            <span>
              After completing a new test, please refresh the page to view the
              most up-to-date results.
            </span>
          </div>
        </CardContent>
      </Card>{" "}
      <AlertDialog
        open={showNoMailboxDialog}
        onOpenChange={setShowNoMailboxDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>No Connected Mailbox</AlertDialogTitle>
            <AlertDialogDescription>
              There are no connected mailboxes for this domain. Would you like
              to test by sending an email?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                navigate({ to: "/domain-health/email-health-test" });
              }}
            >
              Test with Email
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default RunEmailTest;
